import React, { useState, useEffect } from 'react';
import { FilterOptions, getAllQuestions, Question } from '../services/questionService';
import { saveUserAnswer } from '../services/userProgressService';
import { auth, db } from '../services/firebase';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';

interface SimuladosPageProps {
  onBack: () => void;
  filterOptions: FilterOptions;
}

interface SimuladoAnswer {
  questionId: string | number;
  selectedOptionId: string;
  isCorrect: boolean;
}

interface SimuladoResult {
  id: string;
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  accuracy: number;
  timeSpent: number;
  answers: SimuladoAnswer[];
  createdAt: Timestamp;
  filters: {
    subject?: string;
    banca?: string;
    year?: number;
  };
}

interface SimuladoHistoryItem {
  id: string;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: number;
  timeSpent: number;
  createdAt: Timestamp;
  filters: {
    subject?: string;
    banca?: string;
    year?: number;
  };
}

type SimuladoState = 'config' | 'running' | 'review' | 'results';

// Função para salvar simulado no Firestore
const saveSimuladoResult = async (result: SimuladoResult): Promise<void> => {
  const user = auth.currentUser;
  if (!user) return;

  const userSimuladosRef = doc(db, 'userSimulados', user.uid);
  
  try {
    const docSnap = await getDoc(userSimuladosRef);
    
    const historyItem: SimuladoHistoryItem = {
      id: result.id,
      totalQuestions: result.totalQuestions,
      correctAnswers: result.correctAnswers,
      accuracy: result.accuracy,
      timeSpent: result.timeSpent,
      createdAt: result.createdAt,
      filters: result.filters
    };

    if (docSnap.exists()) {
      await updateDoc(userSimuladosRef, {
        history: arrayUnion(historyItem),
        lastSimuladoAt: Timestamp.now()
      });
    } else {
      await setDoc(userSimuladosRef, {
        history: [historyItem],
        lastSimuladoAt: Timestamp.now(),
        createdAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Erro ao salvar simulado:', error);
  }
};

// Função para buscar histórico de simulados
const getSimuladoHistory = async (): Promise<SimuladoHistoryItem[]> => {
  const user = auth.currentUser;
  if (!user) return [];

  const userSimuladosRef = doc(db, 'userSimulados', user.uid);
  
  try {
    const docSnap = await getDoc(userSimuladosRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return (data.history || []).sort((a: SimuladoHistoryItem, b: SimuladoHistoryItem) => 
        b.createdAt.toMillis() - a.createdAt.toMillis()
      );
    }
    return [];
  } catch (error) {
    console.error('Erro ao buscar histórico:', error);
    return [];
  }
};

const SimuladosPage: React.FC<SimuladosPageProps> = ({ onBack, filterOptions }) => {
  // Estado do simulado
  const [simuladoState, setSimuladoState] = useState<SimuladoState>('config');
  const [filters, setFilters] = useState<{ subject?: string; banca?: string; year?: number }>({});
  const [numQuestoes, setNumQuestoes] = useState<number>(20);
  
  // Questões e respostas
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: string]: string }>({});
  const [loading, setLoading] = useState(false);
  
  // Timer
  const [startTime, setStartTime] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  
  // Resultado
  const [result, setResult] = useState<SimuladoResult | null>(null);
  
  // Histórico
  const [history, setHistory] = useState<SimuladoHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Carregar histórico ao montar
  useEffect(() => {
    const loadHistory = async () => {
      const data = await getSimuladoHistory();
      setHistory(data);
      setLoadingHistory(false);
    };
    loadHistory();
  }, []);

  // Timer durante o simulado
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (simuladoState === 'running' && startTime > 0) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [simuladoState, startTime]);

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp: Timestamp): string => {
    const date = timestamp.toDate();
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStartSimulado = async () => {
    setLoading(true);
    try {
      const queryFilters: any = {};
      if (filters.subject) queryFilters.subject = filters.subject;
      if (filters.banca) queryFilters.banca = filters.banca;

      const yearsList = filters.year && filters.year !== "Todos"
        ? Array.from(new Set(filters.year.split(",").map((y) => y.trim()).filter(Boolean)))
        : [];

      let allQuestions: any[] = [];
      if (yearsList.length <= 1) {
        if (yearsList.length === 1) queryFilters.year = yearsList[0];
        allQuestions = await getAllQuestions(queryFilters);
      } else {
        // Busca por ano e concatena (limitado pelo getAllQuestions)
        for (const y of yearsList.slice(0, 10)) {
          const qs = await getAllQuestions({ ...queryFilters, year: y });
          allQuestions = allQuestions.concat(qs || []);
        }
      }

      if (filters.year === "Todos") {
        queryFilters.year = undefined;
      }
      
      // Embaralhar e pegar a quantidade desejada
      const shuffled = allQuestions.sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, numQuestoes);
      
      if (selected.length === 0) {
        alert('Nenhuma questão encontrada com os filtros selecionados.');
        setLoading(false);
        return;
      }

      setQuestions(selected);
      setAnswers({});
      setCurrentQuestionIndex(0);
      setStartTime(Date.now());
      setElapsedTime(0);
      setSimuladoState('running');
    } catch (error) {
      console.error('Erro ao carregar questões:', error);
      alert('Erro ao carregar questões. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (optionId: string) => {
    const currentQuestion = questions[currentQuestionIndex];
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: optionId
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleAbortSimulado = () => {
    if (!window.confirm('Tem certeza que deseja encerrar o simulado agora?')) return;
    setSimuladoState('results');
    setResult(null);
    setQuestions([]);
    setAnswers({});
    setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleGoToQuestion = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const handleFinishSimulado = async () => {
    const totalTime = Math.floor((Date.now() - startTime) / 1000);
    const simuladoId = `simulado_${Date.now()}`;
    
    // Calcular resultados
    const simuladoAnswers: SimuladoAnswer[] = [];
    let correct = 0;
    let wrong = 0;

    for (const question of questions) {
      const selectedOption = answers[question.id];
      const isCorrect = selectedOption === question.correctOptionId;
      
      if (selectedOption) {
        simuladoAnswers.push({
          questionId: question.id,
          selectedOptionId: selectedOption,
          isCorrect
        });
        
        if (isCorrect) {
          correct++;
        } else {
          wrong++;
        }

        // Salvar no progresso do usuário
        if (auth.currentUser) {
          try {
            await saveUserAnswer(
              question.id,
              selectedOption,
              isCorrect,
              Math.floor(totalTime / questions.length),
              question.topic || '',
              question.difficulty || 'média',
              question.examId || '',
              question.subject || ''
            );
          } catch (error) {
            console.error('Erro ao salvar resposta:', error);
          }
        }
      } else {
        // Questão não respondida conta como errada
        wrong++;
        simuladoAnswers.push({
          questionId: question.id,
          selectedOptionId: '',
          isCorrect: false
        });
      }
    }

    const accuracy = questions.length > 0 ? Math.round((correct / questions.length) * 100) : 0;

    const simuladoResult: SimuladoResult = {
      id: simuladoId,
      totalQuestions: questions.length,
      correctAnswers: correct,
      wrongAnswers: wrong,
      accuracy,
      timeSpent: totalTime,
      answers: simuladoAnswers,
      createdAt: Timestamp.now(),
      filters: filters
    };

    // Salvar simulado no Firestore
    await saveSimuladoResult(simuladoResult);

    setResult(simuladoResult);
    setSimuladoState('results');

    // Atualizar histórico local
    setHistory(prev => [{
      id: simuladoId,
      totalQuestions: questions.length,
      correctAnswers: correct,
      accuracy,
      timeSpent: totalTime,
      createdAt: Timestamp.now(),
      filters: filters
    }, ...prev]);
  };

  const handleReviewAnswers = () => {
    setCurrentQuestionIndex(0);
    setSimuladoState('review');
  };

  const handleNewSimulado = () => {
    setSimuladoState('config');
    setQuestions([]);
    setAnswers({});
    setResult(null);
    setCurrentQuestionIndex(0);
  };

  // ==================== RENDERIZAÇÃO ====================

  // Tela de Configuração
  if (simuladoState === 'config') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-100 px-4 py-4 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <h1 className="text-2xl font-black text-gray-900">📝 Meus Simulados</h1>
          </div>
        </header>
        
        <main className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/60">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">📋</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Criar Simulado</h2>
              <p className="text-gray-500 mt-2">Configure sua prova personalizada. Você só verá o gabarito ao finalizar!</p>
            </div>
            
            <div className="space-y-6">
              {/* Número de Questões */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Número de Questões</label>
                <div className="flex gap-2">
                  {[10, 20, 30, 50].map(num => (
                    <button
                      key={num}
                      onClick={() => setNumQuestoes(num)}
                      className={`flex-1 py-3 rounded-xl font-bold transition ${
                        numQuestoes === num 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtro de Matéria */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Matéria</label>
                <select 
                  value={filters.subject || 'Todas'}
                  onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value === 'Todas' ? undefined : e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="Todas">Todas as Matérias</option>
                  {filterOptions.subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              {/* Filtro de Banca */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Banca</label>
                <select 
                  value={filters.banca || 'Todas'}
                  onChange={(e) => setFilters(prev => ({ ...prev, banca: e.target.value === 'Todas' ? undefined : e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="Todas">Todas as Bancas</option>
                  {filterOptions.bancas.map(banca => (
                    <option key={banca} value={banca}>{banca}</option>
                  ))}
                </select>
              </div>

              {/* Filtro de Ano */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Ano</label>
                <select 
                  value={filters.year || 'Todos'}
                  onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value === 'Todos' ? undefined : parseInt(e.target.value) }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="Todos">Todos os Anos</option>
                  {filterOptions.years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <button 
                onClick={handleStartSimulado}
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-bold py-4 rounded-2xl transition flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Carregando...
                  </>
                ) : (
                  <>
                    <span>🚀</span> Iniciar Simulado
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Info sobre o modo simulado */}
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-bold text-amber-800 mb-2">ℹ️ Como funciona o Simulado?</h3>
            <ul className="text-sm text-amber-700 space-y-2">
              <li>• Responda todas as questões <strong>sem ver o gabarito</strong></li>
              <li>• Navegue livremente entre as questões</li>
              <li>• O tempo será cronometrado</li>
              <li>• Ao finalizar, você verá seu desempenho completo</li>
              <li>• Poderá revisar cada questão com a resposta correta</li>
              <li>• Seu histórico de simulados ficará salvo</li>
            </ul>
          </div>

          {/* Histórico de Simulados */}
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">📚 Histórico de Simulados</h2>
            {loadingHistory ? (
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/60 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : history.length === 0 ? (
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/60 text-center">
                <p className="text-gray-500">Você ainda não completou nenhum simulado.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((item, index) => (
                  <div key={item.id || index} className="bg-white/80 backdrop-blur-xl rounded-2xl p-4 shadow-lg border border-white/60 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white ${
                        item.accuracy >= 70 ? 'bg-emerald-500' : item.accuracy >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}>
                        {item.accuracy}%
                      </div>
                      <div>
                        <p className="font-bold text-gray-800">
                          {item.totalQuestions} questões
                          {item.filters?.subject && ` • ${item.filters.subject}`}
                        </p>
                        <p className="text-sm text-gray-500">
                          {item.correctAnswers} acertos • {formatTime(item.timeSpent)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-400">{formatDate(item.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Tela de Simulado em Andamento
  if (simuladoState === 'running') {
    const currentQuestion = questions[currentQuestionIndex];
    const selectedOption = answers[currentQuestion?.id];
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header com Timer */}
        <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center font-black text-white text-lg">📝</div>
              <div>
                <h1 className="font-bold text-gray-900">Simulado em Andamento</h1>
                <p className="text-xs text-gray-500">Questão {currentQuestionIndex + 1} de {questions.length}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Timer */}
              <div className="bg-gray-100 px-4 py-2 rounded-xl">
                <p className="text-xs text-gray-500">Tempo</p>
                <p className="font-bold text-gray-900 font-mono">{formatTime(elapsedTime)}</p>
              </div>

              {/* Progresso */}
              <div className="bg-emerald-100 px-4 py-2 rounded-xl">
                <p className="text-xs text-emerald-600">Respondidas</p>
                <p className="font-bold text-emerald-700">{answeredCount}/{questions.length}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Barra de progresso */}
        <div className="bg-white px-4 py-2 border-b border-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-1">
              {questions.map((q, index) => (
                <button
                  key={q.id}
                  onClick={() => handleGoToQuestion(index)}
                  className={`flex-1 h-2 rounded-full transition ${
                    index === currentQuestionIndex
                      ? 'bg-orange-500'
                      : answers[q.id]
                        ? 'bg-emerald-400'
                        : 'bg-gray-200'
                  }`}
                  title={`Questão ${index + 1}${answers[q.id] ? ' (respondida)' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Questão */}
        <main className="max-w-4xl mx-auto px-4 py-6">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 md:p-8 shadow-2xl border border-white/60">
            {/* Tags da questão */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-semibold">
                Questão {currentQuestionIndex + 1}
              </span>
              {currentQuestion?.subject && (
                <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm">{currentQuestion.subject}</span>
              )}
              {currentQuestion?.banca && (
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">{currentQuestion.banca}</span>
              )}
              {currentQuestion?.year && (
                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">{currentQuestion.year}</span>
              )}
            </div>

            {/* Texto da questão */}
            <p className="text-gray-800 text-lg leading-relaxed mb-8">{currentQuestion?.text}</p>

            {/* Opções - SEM mostrar gabarito */}
            <div className="space-y-3 mb-8">
              {currentQuestion?.options.map((option) => {
                const isSelected = selectedOption === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => handleSelectOption(option.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 transition-all ${
                        isSelected ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {option.id.toUpperCase()}
                      </span>
                      <span className="text-gray-700 pt-1">{option.text}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Navegação - SEM botão de Professor Pobre */}
            <div className="flex gap-3">
              <button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-bold py-4 rounded-xl transition"
              >
                ← Anterior
              </button>
              
              {currentQuestionIndex === questions.length - 1 ? (
                <button
                  onClick={handleFinishSimulado}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 rounded-xl transition"
                >
                  ✓ Finalizar Simulado
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl transition"
                >
                  Próxima →
                </button>
              )}
              <button
                onClick={handleAbortSimulado}
                className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 font-bold py-4 rounded-xl transition border border-red-200"
              >
                Encerrar Simulado
              </button>
            </div>
          </div>

          {/* Navegação rápida */}
          <div className="mt-6 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-sm font-bold text-gray-700 mb-3">Navegação Rápida:</p>
            <div className="flex flex-wrap gap-2">
              {questions.map((q, index) => (
                <button
                  key={q.id}
                  onClick={() => handleGoToQuestion(index)}
                  className={`w-10 h-10 rounded-lg font-bold text-sm transition ${
                    index === currentQuestionIndex
                      ? 'bg-orange-500 text-white'
                      : answers[q.id]
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Tela de Resultados
  if (simuladoState === 'results' && result) {
    const getPerformanceMessage = (accuracy: number): { emoji: string; message: string; color: string } => {
      if (accuracy >= 90) return { emoji: '🏆', message: 'Excelente! Você arrasou!', color: 'text-emerald-600' };
      if (accuracy >= 70) return { emoji: '🎯', message: 'Muito bom! Continue assim!', color: 'text-blue-600' };
      if (accuracy >= 50) return { emoji: '💪', message: 'Bom trabalho! Há espaço para melhorar.', color: 'text-amber-600' };
      if (accuracy >= 0) return { emoji: '📚', message: 'Continue estudando! A prática leva à perfeição.', color: 'text-red-600' };
      return { emoji: '⏹️', message: 'Simulado encerrado sem resultado.', color: 'text-gray-500' };
    };

    const performance = getPerformanceMessage(result.accuracy);

    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-100 px-4 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <h1 className="text-2xl font-black text-gray-900">📊 Resultado do Simulado</h1>
          </div>
        </header>
        
        <main className="max-w-3xl mx-auto px-4 py-8">
          {/* Card Principal */}
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 text-center mb-6">
            <div className="text-6xl mb-4">{performance.emoji}</div>
            <h2 className={`text-2xl font-bold ${performance.color} mb-2`}>{performance.message}</h2>
            
            {/* Gráfico circular */}
            <div className="relative w-48 h-48 mx-auto my-8">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-gray-100"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-emerald-500"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray={`${result.accuracy}, 100`}
                  strokeLinecap="round"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-black text-gray-800">{result.accuracy}%</span>
                <span className="text-xs font-bold text-gray-400 uppercase">Aproveitamento</span>
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-black text-gray-800">{result.totalQuestions}</p>
              <p className="text-xs text-gray-500 uppercase">Questões</p>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-black text-emerald-600">{result.correctAnswers}</p>
              <p className="text-xs text-gray-500 uppercase">Acertos</p>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-black text-red-500">{result.wrongAnswers}</p>
              <p className="text-xs text-gray-500 uppercase">Erros</p>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 text-center">
              <p className="text-2xl font-black text-blue-600">{formatTime(result.timeSpent)}</p>
              <p className="text-xs text-gray-500 uppercase">Tempo</p>
            </div>
          </div>

          {/* Ações */}
          <div className="space-y-3">
            <button
              onClick={handleReviewAnswers}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-2xl transition flex items-center justify-center gap-2"
            >
              <span>🔍</span> Revisar Respostas
            </button>
            <button
              onClick={handleNewSimulado}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-2xl transition flex items-center justify-center gap-2"
            >
              <span>📝</span> Novo Simulado
            </button>
            <button
              onClick={onBack}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-4 rounded-2xl transition"
            >
              Voltar ao Início
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Tela de Revisão
  if (simuladoState === 'review') {
    const currentQuestion = questions[currentQuestionIndex];
    const selectedOption = answers[currentQuestion?.id];
    const isCorrect = selectedOption === currentQuestion?.correctOptionId;

    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSimuladoState('results')} className="p-2 hover:bg-gray-100 rounded-lg transition">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div>
                <h1 className="font-bold text-gray-900">Revisão do Simulado</h1>
                <p className="text-xs text-gray-500">Questão {currentQuestionIndex + 1} de {questions.length}</p>
              </div>
            </div>
            <div className={`px-4 py-2 rounded-xl font-bold ${isCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {isCorrect ? '✓ Correta' : '✗ Incorreta'}
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-6">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-6 md:p-8 shadow-2xl border border-white/60">
            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${isCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                Questão {currentQuestionIndex + 1}
              </span>
              {currentQuestion?.subject && (
                <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm">{currentQuestion.subject}</span>
              )}
            </div>

            {/* Texto */}
            <p className="text-gray-800 text-lg leading-relaxed mb-8">{currentQuestion?.text}</p>

            {/* Opções com gabarito */}
            <div className="space-y-3 mb-8">
              {currentQuestion?.options.map((option) => {
                const isSelected = selectedOption === option.id;
                const isCorrectOption = option.id === currentQuestion.correctOptionId;
                
                let optionClass = 'border-gray-200';
                if (isCorrectOption) {
                  optionClass = 'border-emerald-500 bg-emerald-50';
                } else if (isSelected && !isCorrectOption) {
                  optionClass = 'border-red-500 bg-red-50';
                }

                return (
                  <div
                    key={option.id}
                    className={`p-4 rounded-xl border-2 ${optionClass}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                        isCorrectOption 
                          ? 'bg-emerald-500 text-white' 
                          : isSelected 
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isCorrectOption ? '✓' : isSelected ? '✗' : option.id.toUpperCase()}
                      </span>
                      <span className="text-gray-700 pt-1">{option.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Comentário/Explicação */}
            {currentQuestion?.comments && (
              <div className="p-6 bg-blue-50 rounded-2xl border border-blue-200 mb-8">
                <p className="font-bold text-blue-800 mb-2">💡 Explicação:</p>
                <p className="text-blue-700">{currentQuestion.comments}</p>
              </div>
            )}

            {/* Navegação */}
            <div className="flex gap-3">
              <button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-bold py-4 rounded-xl transition"
              >
                ← Anterior
              </button>
              
              {currentQuestionIndex === questions.length - 1 ? (
                <button
                  onClick={() => setSimuladoState('results')}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-xl transition"
                >
                  Ver Resultado
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-xl transition"
                >
                  Próxima →
                </button>
              )}
            </div>
          </div>

          {/* Navegação rápida na revisão */}
          <div className="mt-6 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-sm font-bold text-gray-700 mb-3">Navegação:</p>
            <div className="flex flex-wrap gap-2">
              {questions.map((q, index) => {
                const wasCorrect = answers[q.id] === q.correctOptionId;
                return (
                  <button
                    key={q.id}
                    onClick={() => handleGoToQuestion(index)}
                    className={`w-10 h-10 rounded-lg font-bold text-sm transition ${
                      index === currentQuestionIndex
                        ? 'bg-blue-500 text-white'
                        : wasCorrect
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {index + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return null;
};

export default SimuladosPage;
